/**
 * calendarTables.js - the Manpower calendar's tables (10/16/26): which grids
 * the calendar draws, in what order, who sits in each, and which count toward
 * utilization.
 *
 * Server twin: server/lib/calendarTables.js - keep the two in step (its test,
 * server/test_calendar_tables.js, and __tests__/calendarTables.test.js run the
 * same cases).
 *
 * A layout is { tables: [{ id, name, position, countsUtilization }],
 * members: { engineerId: tableId } } (GET /api/manpower/tables). `members`
 * holds only the people someone moved; everyone else sits where their
 * discipline put them before the tables could be edited (data/disciplines.js
 * calendarGroupOf: controls / mechanical -> 'controls', else 'process'), and
 * when that table is gone, in the first table. PURE.
 */
import { CALENDAR_GROUPS, calendarGroupOf } from '../../data/disciplines';
import { resourceGroupOf } from '../../data/resourceGroups';

/** Today's layout before anyone edits it - the server seeds the same rows. */
export const DEFAULT_LAYOUT = Object.freeze({
  tables: CALENDAR_GROUPS.map((g, i) => ({ id: g.key, name: g.title, position: i, countsUtilization: true })),
  members: {},
});

/** The tables in drawing order (position, then name). */
export const orderedTables = (layout) =>
  [...((layout && layout.tables) || [])].sort(
    (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.name).localeCompare(String(b.name))
  );

/** GET /api/manpower/tables' answer as a layout; anything else is the default layout. */
export function layoutOf(data) {
  if (!data || !Array.isArray(data.tables) || !data.tables.length) return DEFAULT_LAYOUT;
  return {
    tables: data.tables
      .filter((t) => t && t.id)
      .map((t) => ({
        id: String(t.id),
        name: String(t.name || ''),
        position: Number(t.position) || 0,
        countsUtilization: !!t.countsUtilization,
      })),
    members: data.members && typeof data.members === 'object' ? { ...data.members } : {},
  };
}

/** The table id this engineer is drawn in, or null when there are no tables. */
export function tableOf(engineer, layout = DEFAULT_LAYOUT) {
  const tables = orderedTables(layout);
  if (!tables.length) return null;
  const ids = new Set(tables.map((t) => t.id));
  const members = (layout && layout.members) || {};
  const moved = engineer && engineer.id != null ? members[engineer.id] : null;
  if (moved && ids.has(moved)) return moved;
  const byDiscipline = calendarGroupOf(engineer && engineer.discipline);
  if (ids.has(byDiscipline)) return byDiscipline;
  return tables[0].id;
}

/** Employment type Intern, however it was typed. */
export const isIntern = (engineer) =>
  String((engineer && (engineer.employmentType ?? engineer.employment_type)) || '')
    .trim()
    .toLowerCase() === 'intern';

/**
 * True when this engineer is an INTERNAL engineer (data/resourceGroups.js -
 * not an intern, contractor, South Africa, MENA or Europe) and their table has
 * "Counts toward utilization" ON (Sharif, 10/16/26).
 */
export function countsTowardUtilization(engineer, layout = DEFAULT_LAYOUT) {
  if (!engineer || resourceGroupOf(engineer) !== 'internal') return false;
  const id = tableOf(engineer, layout);
  const t = ((layout && layout.tables) || []).find((x) => x.id === id);
  return !!(t && t.countsUtilization);
}

/** [{ ...table, engineers }] in drawing order, each engineer in exactly one. */
export function sectionsOf(engineers, layout = DEFAULT_LAYOUT) {
  const tables = orderedTables(layout);
  const by = new Map(tables.map((t) => [t.id, []]));
  for (const e of engineers || []) {
    const id = tableOf(e, layout);
    if (by.has(id)) by.get(id).push(e);
  }
  return tables.map((t) => ({ ...t, engineers: by.get(t.id) }));
}
